import {
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateTime } from 'luxon';
import { SelectOption } from '../../../types';
import {
  APPLICATION_TIME_ZONE,
  getApplicationTimeZoneName,
  toApplicationDateTime,
} from '../../date-time';
import { FormFieldAbstractComponent } from '../form-field-abstract.component';

@Component({
  selector: 'app-form-field-datetime',
  templateUrl: './form-field-datetime.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class FormFieldDatetimeComponent
  extends FormFieldAbstractComponent
  implements OnInit
{
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;
  dateOnlyValue = new FormControl('');
  selectedDate: Date | null = null;
  hours: FormControl<string | null> = new FormControl<
    string | null
  >('00');
  hoursOptions: SelectOption[] = [];
  minutes: FormControl<string | null> = new FormControl<
    string | null
  >('00');
  minutesOptions: SelectOption[] = [];
  seconds: FormControl<string | null> = new FormControl<
    string | null
  >('00');
  secondsOptions: SelectOption[] = [];

  get timezoneName(): string {
    const date = this.selectedDate
      ? toApplicationDateTime(this.selectedDate)
      : DateTime.now().setZone(APPLICATION_TIME_ZONE);
    return getApplicationTimeZoneName(date);
  }

  ngOnInit(): void {
    this.secondsOptions = this.createSelectOptions(59);
    this.minutesOptions = this.createSelectOptions(59);
    this.hoursOptions = this.createSelectOptions(23);

    const dateTime = toApplicationDateTime(
      this.field?.value
    );
    if (dateTime.isValid) {
      this.selectedDate = dateTime.toJSDate();

      this.hours.setValue(this.pad(dateTime.hour));
      this.minutes.setValue(this.pad(dateTime.minute));
      this.seconds.setValue(this.pad(dateTime.second));

      this.dateOnlyValue.setValue(dateTime.toISODate(), {
        emitEvent: false,
      });
    }

    this.dateOnlyValue.valueChanges.subscribe(() =>
      this.updateDateTime()
    );
    this.hours.valueChanges.subscribe(() =>
      this.updateDateTime()
    );
    this.minutes.valueChanges.subscribe(() =>
      this.updateDateTime()
    );
    this.seconds.valueChanges.subscribe(() =>
      this.updateDateTime()
    );
  }

  private pad(value: number): string {
    return String(value).padStart(2, '0');
  }

  private updateDateTime(): void {
    const rawDate = this.dateOnlyValue.value;
    if (typeof rawDate === 'string' && rawDate.trim()) {
      const parts = rawDate.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);

        const dateTime = DateTime.fromObject(
          {
            year,
            month,
            day,
            hour: Number(this.hours.value),
            minute: Number(this.minutes.value),
            second: Number(this.seconds.value),
          },
          { zone: APPLICATION_TIME_ZONE }
        );

        this.hours.setValue(this.pad(dateTime.hour), {
          emitEvent: false,
        });
        this.minutes.setValue(this.pad(dateTime.minute), {
          emitEvent: false,
        });
        this.seconds.setValue(this.pad(dateTime.second), {
          emitEvent: false,
        });
        this.selectedDate = dateTime.toJSDate();
        this.field.setValue(dateTime.toISO(), {
          emitEvent: false,
        });
      }
    }
  }

  private createSelectOptions(
    count: number
  ): SelectOption[] {
    const options: SelectOption[] = [];
    for (let i = 0; i <= count; i++) {
      const stringValue = i > 9 ? `${i}` : `0${i}`;
      options.push({
        value: stringValue,
        label: stringValue,
      } as SelectOption);
    }
    return options;
  }
}
